import React, {useState} from 'react';
import {Box, Container, Divider, HStack, Skeleton, VStack, useTheme} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../components/typography';
import TabSwitcher from '../../components/TabSwitcher';
import useAuth from '../../hooks/useAuth';
import ProfileHeader from './components/ProfileHeader';
import ProfileCard from './components/ProfileCard';
import ProfileProgress from './components/ProfileProgress';
import ProfilePreview from './components/ProfilePreview';
import InsiderUpsellCard from './components/InsiderUpsellCard';
import EditNicknameModal from './modals/EditNicknameModal';
import EditNeighborhoodModal from './modals/EditNeighborhoodModal';
import EditIntroductionModal from './modals/EditIntroductionModal';
import ConversationStartersModal from './modals/ConversationStartersModal';
import EditAvailabilityModal from './modals/EditAvailabilityModal';
import EditMyTimeModal from './modals/EditMyTimeModal';
import {useConversationStarters} from '../../hooks/useConversationStarters';
import {useAvailability} from '../../hooks/useAvailability';
import {useCityProfile} from '../../hooks/useCityProfile';
import {DAY_ABBREV} from '../../utils/availability';
import {DURATION_LABELS} from '../../utils/cityDurationLabels';

type ActiveTab = 'edit' | 'preview';

type ModalType =
  | 'nickname'
  | 'neighborhood'
  | 'introduction'
  | 'conversation_starters'
  | 'availability'
  | 'my_time'
  | null;

interface ProfileFieldRowProps {
  label: string;
  description?: React.ReactNode;
  value?: string;
  isLoading?: boolean;
  onClick: () => void;
}

const ProfileFieldRow = ({label, description, value, isLoading, onClick}: ProfileFieldRowProps) => {
  const theme = useTheme();
  return (
    <HStack
      as="button"
      w="100%"
      justify="space-between"
      align="flex-start"
      px={4}
      py={4}
      onClick={onClick}
      _active={{bg: '#FAFAFA'}}
      borderBottom="1px solid #F0F0F0">
      <VStack align="flex-start" spacing={1} flex={1} minW={0}>
        <TextG14 mt="0" fontWeight="600">
          {label}
        </TextG14>
        {isLoading ? (
          <Skeleton height="14px" width="180px" borderRadius="4px" />
        ) : description ? (
          typeof description === 'string' ? (
            <TextG12 mt="0" color="#757D84" noOfLines={2} textAlign="left">
              {description}
            </TextG12>
          ) : (
            <Box w="100%">{description}</Box>
          )
        ) : null}
      </VStack>
      <i
        className="fi fi-rr-angle-small-right"
        aria-hidden="true"
        style={{fontSize: '14px', color: theme.colors.grey, marginTop: '2px', flexShrink: 0}}
      />
    </HStack>
  );
};

function MyProfile() {
  const {user} = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('edit');
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const {data: conversationStarters = [], isLoading: isLoadingStarters} = useConversationStarters();
  const {data: availability, isLoading: isLoadingAvailability} = useAvailability();
  const {data: cityProfile, isLoading: isLoadingCityProfile} = useCityProfile();

  const conversationStartersDescription = (() => {
    if (conversationStarters.length === 0) return '';
    return (
      <VStack align="flex-start" spacing={2} w="100%">
        {conversationStarters.map((s, i) => (
          <React.Fragment key={s.prompt_id ?? i}>
            {i > 0 && <Divider borderColor="#E0E0E0" />}
            <TextG12 mt="0" color="#757D84" textAlign="left" noOfLines={1}>
              {s.prompt?.text ? `${s.prompt.text} ${s.answer}` : s.answer}
            </TextG12>
          </React.Fragment>
        ))}
      </VStack>
    );
  })();

  const availabilityDescription = (() => {
    if (!availability) return '';
    const parts: string[] = [];
    if (availability.general?.length) {
      parts.push(...availability.general.map((s: string) => `${s}s`));
    }
    if (availability.specific) {
      Object.entries(availability.specific).forEach(([day, times]) => {
        (times as string[]).forEach((t) => {
          parts.push(`${DAY_ABBREV[day] || day} ${t}`);
        });
      });
    }
    return parts.join(', ');
  })();

  const cityProfileDescription = (() => {
    if (!cityProfile) return '';
    const parts: string[] = [];
    if (cityProfile.city_duration) parts.push(DURATION_LABELS[cityProfile.city_duration] || cityProfile.city_duration);
    if (cityProfile.neighborhood_duration) parts.push(`${DURATION_LABELS[cityProfile.neighborhood_duration] || cityProfile.neighborhood_duration} in current neighborhood`);
    if (cityProfile.home_duration) parts.push(`${DURATION_LABELS[cityProfile.home_duration] || cityProfile.home_duration} in current home`);
    if (cityProfile.is_renting !== null) parts.push(cityProfile.is_renting ? 'Rent' : 'Own');
    return parts.join(', ');
  })();

  const isMember = user?.hasActiveMembership;

  return (
    <Container p={0} maxW="100%" bg="#FAFAFA" minH="100vh">
      <Box bg="white">
        <ProfileHeader />

        {/* Edit / Preview tabs */}
        <Box pt={3}>
          <TabSwitcher
            ariaLabel="Profile sections"
            activeTab={activeTab}
            onChange={setActiveTab}
            tabs={[
              {value: 'edit', label: 'Edit', panelId: 'my-profile-edit-panel'},
              {value: 'preview', label: 'Preview', panelId: 'my-profile-preview-panel'},
            ]}
          />
        </Box>
      </Box>

      {activeTab === 'edit' ? (
        <Box id="my-profile-edit-panel" role="tabpanel" pt={4} pb={20}>
          {/* Profile Card section */}
          <ProfileCard
            onNicknameClick={() => setActiveModal('nickname')}
            onAddressClick={() => setActiveModal('neighborhood')}
          />

          {/* Profile Progress */}
          <ProfileProgress />

          {/* Field rows */}
          <Box mx={4} bg="white" borderRadius="16px" border="1px solid #EBEBEB" overflow="hidden" mb={4} boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
            <ProfileFieldRow
              label="My Introduction"
              description={
                user?.aboutMe ? (
                  <VStack align="flex-start" spacing={1} w="100%">
                    <TextG12 mt="0" color="#757D84" textAlign="left">
                      I could happily nerd out about...
                    </TextG12>
                    <TextG12 mt="0" color="#1A1A1A" fontWeight="600" textAlign="left" noOfLines={2}>
                      {user.aboutMe}
                    </TextG12>
                  </VStack>
                ) : ''
              }
              onClick={() => setActiveModal('introduction')}
            />
            <ProfileFieldRow
              label="Conversation Starters"
              description={conversationStartersDescription}
              isLoading={isLoadingStarters}
              onClick={() => setActiveModal('conversation_starters')}
            />
            <ProfileFieldRow
              label="My Availability"
              description={availabilityDescription}
              isLoading={isLoadingAvailability}
              onClick={() => setActiveModal('availability')}
            />
            <Box borderBottom="none">
              <ProfileFieldRow
                label="My Time in San Diego"
                description={cityProfileDescription}
                isLoading={isLoadingCityProfile}
                onClick={() => setActiveModal('my_time')}
              />
            </Box>
          </Box>

          {/* Insider upsell — only for non-members */}
          {!isMember && <InsiderUpsellCard />}
        </Box>
      ) : (
        <Box id="my-profile-preview-panel" role="tabpanel">
          <ProfilePreview />
        </Box>
      )}

      {/* Modals */}
      <EditNicknameModal
        isOpen={activeModal === 'nickname'}
        onClose={() => setActiveModal(null)}
      />
      <EditNeighborhoodModal
        isOpen={activeModal === 'neighborhood'}
        onClose={() => setActiveModal(null)}
      />
      <EditIntroductionModal
        isOpen={activeModal === 'introduction'}
        onClose={() => setActiveModal(null)}
      />
      <ConversationStartersModal
        isOpen={activeModal === 'conversation_starters'}
        onClose={() => setActiveModal(null)}
      />
      <EditAvailabilityModal
        isOpen={activeModal === 'availability'}
        onClose={() => setActiveModal(null)}
      />
      <EditMyTimeModal
        isOpen={activeModal === 'my_time'}
        onClose={() => setActiveModal(null)}
      />
    </Container>
  );
}

export default MyProfile;
